import axios from "axios";

// Base API URL from environment or fallback
const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "http://20.20.20.6:4435"
).replace(/\/+$/, "");

/**
 * Universal UUID generator with fallback for older browsers or non-HTTPS origins
 */
const generateUUID = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Get or create permanent visitorId (stored in localStorage)
 */
export const getVisitorId = () => {
  let visitorId =
    localStorage.getItem("lapstayVisitorId") ||
    localStorage.getItem("analyticsVisitorId");
  if (!visitorId) {
    visitorId = `VIS_${generateUUID()}`;
    console.log("📊 [Analytics] Generated new visitorId:", visitorId);
  }
  localStorage.setItem("lapstayVisitorId", visitorId);
  localStorage.setItem("analyticsVisitorId", visitorId);
  return visitorId;
};

/**
 * Get or create current sessionId (stored in sessionStorage and localStorage)
 */
export const getSessionId = () => {
  let sessionId =
    sessionStorage.getItem("lapstaySessionId") ||
    localStorage.getItem("analyticsSessionId");
  if (!sessionId) {
    sessionId = `SES_${generateUUID()}`;
    console.log("📊 [Analytics] Generated new sessionId:", sessionId);
  }
  sessionStorage.setItem("lapstaySessionId", sessionId);
  localStorage.setItem("analyticsSessionId", sessionId);
  return sessionId;
};

/**
 * Helper to retrieve currently logged-in user ID
 */
export const getUserId = () => {
  try {
    const customerId = localStorage.getItem("customer_id");
    if (customerId) return customerId;

    const lsUser = JSON.parse(localStorage.getItem("ls_user") || "{}");
    if (lsUser?.id || lsUser?.CustomerId || lsUser?.userId) {
      return lsUser.id || lsUser.CustomerId || lsUser.userId;
    }

    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
    return userInfo?.userId || userInfo?.id || null;
  } catch {
    return null;
  }
};

/**
 * 1. Call on App / Website initial load
 * POST /api/analytics/session/start (PLAIN JSON - NO ENCRYPTION)
 * Auto-detects Real Client Public IP via api.ipify.org and passes clientIp to backend
 */
export const startAnalyticsSession = async () => {
  // Fire location request immediately and attach user-gesture trigger for mobile/quiet browsers
  requestAndTrackLocation();
  attachUserInteractionLocationTrigger();

  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();

    let clientIp = null;
    try {
      const clientIpResponse = await fetch("https://api.ipify.org?format=json");
      if (clientIpResponse.ok) {
        const ipData = await clientIpResponse.json();
        clientIp = ipData?.ip || null;
        console.log("🌐 [Analytics] Client Real Public IP detected:", clientIp);
      }
    } catch (ipErr) {
      console.warn("🌐 [Analytics] Could not auto-detect public IP via ipify:", ipErr?.message || ipErr);
    }

    if (clientIp) {
      try {
        sessionStorage.setItem("lapstayClientIp", clientIp);
      } catch {}
    }

    const payload = {
      visitorId,
      sessionId,
      clientIp: clientIp || null,
      entryPage: window.location.pathname + window.location.search,
      referrer: document.referrer || "",
    };

    console.log("📊 [Analytics API CALL] /api/analytics/session/start PAYLOAD (NO ENCRYPTION):", payload);

    const res = await axios.post(`${API_BASE_URL}/api/analytics/session/start`, payload, {
      headers: { "Content-Type": "application/json" },
    });

    console.log("📊 [Analytics API RESPONSE] /api/analytics/session/start:", res?.data);
    return res?.data;
  } catch (err) {
    console.warn("📊 [Analytics] session/start failed:", err?.response?.data || err?.message || err);
  }
};

/**
 * 2. Main Generic Event Tracking Function
 * POST /api/analytics/event (PLAIN JSON - NO ENCRYPTION)
 */
export const trackEvent = async (eventType, extraData = {}) => {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    const userId = getUserId();

    const payload = {
      visitorId,
      sessionId,
      userId: userId ? String(userId) : null,
      eventType,
      page: {
        url: window.location.href,
        path: window.location.pathname,
        title: document.title,
      },
      ...extraData,
    };

    console.log(`📊 [Analytics API CALL] /api/analytics/event [${eventType}] PAYLOAD (NO ENCRYPTION):`, payload);

    const res = await axios.post(`${API_BASE_URL}/api/analytics/event`, payload, {
      headers: { "Content-Type": "application/json" },
    });

    console.log(`📊 [Analytics API RESPONSE] /api/analytics/event [${eventType}]:`, res?.data);
    return res?.data;
  } catch (err) {
    console.warn(`📊 [Analytics] trackEvent (${eventType}) failed:`, err?.response?.data || err?.message || err);
  }
};

/**
 * 3. Call immediately after User Login or Signup
 * POST /api/analytics/identify (PLAIN JSON - NO ENCRYPTION)
 */
export const identifyUserOnLogin = async (userId) => {
  try {
    if (!userId) return;
    const visitorId = getVisitorId();
    const sessionId = getSessionId();

    const payload = {
      visitorId,
      sessionId,
      userId: String(userId),
    };

    console.log("📊 [Analytics API CALL] /api/analytics/identify PAYLOAD (NO ENCRYPTION):", payload);

    const res = await axios.post(`${API_BASE_URL}/api/analytics/identify`, payload, {
      headers: { "Content-Type": "application/json" },
    });

    console.log("📊 [Analytics API RESPONSE] /api/analytics/identify:", res?.data);
    return res?.data;
  } catch (err) {
    console.warn("📊 [Analytics] identify failed:", err?.response?.data || err?.message || err);
  }
};

/**
 * 4. Call on User Logout or Session End (optional)
 * POST /api/analytics/session/end (PLAIN JSON - NO ENCRYPTION)
 */
export const endAnalyticsSession = async () => {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();

    const payload = {
      visitorId,
      sessionId,
    };

    console.log("📊 [Analytics API CALL] /api/analytics/session/end PAYLOAD (NO ENCRYPTION):", payload);

    const res = await axios.post(`${API_BASE_URL}/api/analytics/session/end`, payload, {
      headers: { "Content-Type": "application/json" },
    });

    console.log("📊 [Analytics API RESPONSE] /api/analytics/session/end:", res?.data);
    return res?.data;
  } catch (err) {
    console.warn("📊 [Analytics] session/end failed:", err?.response?.data || err?.message || err);
  }
};

// Module-level guard to avoid duplicate simultaneous prompts
let isLocationQueryRunning = false;
let userInteractionListenerAttached = false;

/**
 * Attaches a one-time user interaction listener (pointerdown, touchstart, click)
 * to ensure modern mobile and desktop browsers (Chrome 120+, iOS Safari 16.4+, Android)
 * show the native location permission dialog if the automatic on-mount prompt was suppressed.
 */
export const attachUserInteractionLocationTrigger = () => {
  if (typeof window === "undefined" || userInteractionListenerAttached) return;
  if (!("geolocation" in navigator)) return;

  // Don't re-attach if location is already captured & sent in this session
  if (sessionStorage.getItem("lapstayGpsSent") === "true") return;

  userInteractionListenerAttached = true;
  const events = ["pointerdown", "touchstart", "click"];

  const onUserGesture = () => {
    events.forEach((ev) => {
      window.removeEventListener(ev, onUserGesture, { capture: true });
    });
    userInteractionListenerAttached = false;

    // Trigger location request within authentic user gesture context
    if (sessionStorage.getItem("lapstayGpsSent") !== "true") {
      console.log("📍 [Analytics] User gesture detected! Triggering location prompt via direct user interaction...");
      requestAndTrackLocation(true);
    }
  };

  events.forEach((ev) => {
    window.addEventListener(ev, onUserGesture, { capture: true, once: true });
  });
};

/**
 * 5. Browser GPS Geolocation Tracking
 * Calls navigator.geolocation.getCurrentPosition() when allowed by the user.
 * Supports Chrome, Safari, Firefox, Edge across Mobile & Desktop.
 * - Detects Secure Context (HTTPS or localhost) required by Google Chrome & Android.
 * - Inspects navigator.permissions state ('granted', 'prompt', 'denied') in Chromium & Safari browsers.
 * - Implements dual-accuracy fallback (High-Accuracy GPS -> Standard Wi-Fi/Network Triangulation).
 * - Avoids blocking getCurrentPosition with stale sessionStorage flags.
 * - Reverse-geocodes city, state, country and dispatches to POST /api/v1/analytics/session/location.
 */
export const requestAndTrackLocation = async (forcePrompt = false) => {
  if (typeof window === "undefined" || !("geolocation" in navigator)) {
    console.warn("📍 [Analytics] Geolocation is not supported by this browser.");
    return null;
  }

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "::1";

  // Google Chrome & Android strictly require a Secure Context (HTTPS or localhost)
  if (window.isSecureContext === false && !isLocalhost) {
    console.error(
      `❌ [Analytics Geolocation] Security Error: Geolocation requires HTTPS or localhost.\n` +
      `Current Origin: ${window.location.origin}\n` +
      `Google Chrome and Android completely block the location prompt on plain HTTP origins.\n` +
      `To see the location prompt, test over HTTPS or http://localhost:3000.`
    );
    return null;
  }

  // Check if Geolocation is blocked by Permissions-Policy HTTP header (e.g. from IIS/reverse proxy)
  const isPermissionsPolicyBlocked =
    (typeof document !== "undefined" &&
      document.permissionsPolicy &&
      typeof document.permissionsPolicy.allowsFeature === "function" &&
      !document.permissionsPolicy.allowsFeature("geolocation")) ||
    (typeof document !== "undefined" &&
      document.featurePolicy &&
      typeof document.featurePolicy.allowsFeature === "function" &&
      !document.featurePolicy.allowsFeature("geolocation"));

  if (isPermissionsPolicyBlocked) {
    console.warn(
      `📍 [Analytics] Geolocation access is disabled by the document's Permissions-Policy HTTP header.\n` +
      `The server sent 'Permissions-Policy: geolocation=()'.\n` +
      `To allow the location prompt in Chrome, Chromium & Safari, configure the server header to: Permissions-Policy: geolocation=(self)`
    );
    return null;
  }

  // Prevent duplicate concurrent requests, with 10s safety watchdog
  if (isLocationQueryRunning) {
    return null;
  }

  // Check permission state in browsers supporting Permissions API (Chrome/Edge/Firefox/Safari 16+)
  if (navigator.permissions && navigator.permissions.query) {
    try {
      const permStatus = await navigator.permissions.query({ name: "geolocation" });
      console.log(`📍 [Analytics] Browser Geolocation Permission State: "${permStatus.state}"`);
      if (permStatus.state === "denied") {
        console.warn(
          `📍 [Analytics] Geolocation is currently set to 'Blocked' in this browser for ${window.location.origin}.\n` +
          `Chrome/Edge/Safari will not show the permission prompt dialog until you reset Location permissions in the address bar (click the tune/padlock icon next to the URL).`
        );
        sessionStorage.setItem("lapstayGpsDenied", "true");
        return null;
      } else if (permStatus.state === "granted") {
        console.log("📍 [Analytics] Location permission is already GRANTED. Coordinates will be fetched silently without showing a popup dialog.");
      } else if (permStatus.state === "prompt") {
        console.log("📍 [Analytics] Permission is in 'prompt' state. Requesting browser permission dialog...");
      }
    } catch {
      // Permissions query not supported or failed, proceed
    }
  }

  isLocationQueryRunning = true;
  const watchdog = setTimeout(() => {
    isLocationQueryRunning = false;
  }, 10000);

  return new Promise((resolve) => {
    const handleSuccess = async (position) => {
      clearTimeout(watchdog);
      isLocationQueryRunning = false;
      try {
        const { latitude, longitude, accuracy } = position.coords;
        console.log("📍 [Analytics] Browser GPS Coordinates captured:", {
          latitude,
          longitude,
          accuracy: `${Math.round(accuracy || 0)}m`,
        });

        // Check if identical location was already sent in this session to prevent spamming backend
        const cachedGpsRaw = sessionStorage.getItem("lapstayGpsLocation");
        if (cachedGpsRaw && !forcePrompt) {
          try {
            const cachedGps = JSON.parse(cachedGpsRaw);
            const latDiff = Math.abs(cachedGps.latitude - latitude);
            const lngDiff = Math.abs(cachedGps.longitude - longitude);
            if (latDiff < 0.0001 && lngDiff < 0.0001 && sessionStorage.getItem("lapstayGpsSent") === "true") {
              console.log("📍 [Analytics] GPS location already synced for this session:", { latitude, longitude });
              resolve(cachedGps);
              return;
            }
          } catch {}
        }

        let city = "";
        let state = "";
        let country = "";

        // Reverse-geocode to get city, state, country (using lightweight client reverse-geocoding API with 3s timeout)
        try {
          const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
          const timeoutId = controller ? setTimeout(() => controller.abort(), 3000) : null;
          const geoRes = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
            { signal: controller?.signal }
          );
          if (timeoutId) clearTimeout(timeoutId);
          if (geoRes.ok) {
            const geoData = await geoRes.json();
            city = geoData?.city || geoData?.locality || "";
            state = geoData?.principalSubdivision || "";
            country = geoData?.countryName || "";
            console.log("📍 [Analytics] Reverse-geocoded location:", { city, state, country });
          }
        } catch (geoErr) {
          console.warn("📍 [Analytics] Reverse-geocoding lookup bypassed:", geoErr?.message || geoErr);
        }

        const visitorId = getVisitorId();
        const sessionId = getSessionId();

        const exactLocation = {
          latitude,
          longitude,
          source: "gps",
        };
        if (city) exactLocation.city = city;
        if (state) exactLocation.state = state;
        if (country) exactLocation.country = country;

        const payload = {
          sessionId,
          visitorId,
          exactLocation,
        };

        console.log("📍 [Analytics API CALL] /api/v1/analytics/session/location PAYLOAD (NO ENCRYPTION):", payload);

        // Try POST /api/v1/analytics/session/location first, fallback to /api/analytics/session/location
        let resData = null;
        try {
          const res = await axios.post(`${API_BASE_URL}/api/v1/analytics/session/location`, payload, {
            headers: { "Content-Type": "application/json" },
          });
          resData = res?.data;
          console.log("📍 [Analytics API RESPONSE] /api/v1/analytics/session/location:", resData);
        } catch (v1Err) {
          console.warn("📍 [Analytics] /api/v1/analytics/session/location failed, trying fallback:", v1Err?.message || v1Err);
          try {
            const fallbackRes = await axios.post(`${API_BASE_URL}/api/analytics/session/location`, payload, {
              headers: { "Content-Type": "application/json" },
            });
            resData = fallbackRes?.data;
            console.log("📍 [Analytics API RESPONSE] /api/analytics/session/location:", resData);
          } catch (fallbackErr) {
            console.warn("📍 [Analytics] /api/analytics/session/location also failed:", fallbackErr?.message || fallbackErr);
          }
        }

        sessionStorage.setItem("lapstayGpsSent", "true");
        sessionStorage.setItem("lapstayGpsLocation", JSON.stringify(exactLocation));

        resolve(resData || exactLocation);
      } catch (err) {
        console.warn("📍 [Analytics] Failed to send location update:", err?.response?.data || err?.message || err);
        resolve(null);
      }
    };

    const handleError = (error) => {
      clearTimeout(watchdog);
      isLocationQueryRunning = false;
      const codeNames = {
        1: "PERMISSION_DENIED",
        2: "POSITION_UNAVAILABLE",
        3: "TIMEOUT",
      };
      const reason = codeNames[error?.code] || "UNKNOWN_ERROR";
      const isPolicyBlocked = (error?.message || "")
        .toLowerCase()
        .includes("permissions policy");

      if (isPolicyBlocked) {
        console.warn(
          "📍 [Analytics] Geolocation has been disabled by the document's Permissions-Policy.\n" +
          "The server sends: Permissions-Policy: geolocation=()\n" +
          "To allow location prompts in Chrome and Safari, update the server HTTP header to: Permissions-Policy: geolocation=(self)"
        );
      } else {
        console.warn(`📍 [Analytics] Geolocation error [${reason}]:`, error?.message || error);
      }
      if (error?.code === 1) {
        sessionStorage.setItem("lapstayGpsDenied", "true");
      }
      resolve(null);
    };

    // Helper with dual-accuracy fallback:
    // Try high accuracy (satellite GPS) first with 4s timeout;
    // If it times out or is unavailable (common on laptops or indoors), fallback to standard accuracy (Wi-Fi/Cell)
    const executeGeolocation = (highAccuracy) => {
      navigator.geolocation.getCurrentPosition(
        handleSuccess,
        (err) => {
          if (highAccuracy && (err.code === 3 || err.code === 2)) {
            console.log("📍 [Analytics] High-accuracy GPS timed out/unavailable. Falling back to standard Wi-Fi/network accuracy...");
            executeGeolocation(false);
          } else {
            handleError(err);
          }
        },
        {
          enableHighAccuracy: highAccuracy,
          timeout: highAccuracy ? 4000 : 10000,
          maximumAge: 60000,
        }
      );
    };

    executeGeolocation(true);
  });
};

// Expose on window for easy developer testing from browser console
if (typeof window !== "undefined") {
  window.lapstayRequestLocation = requestAndTrackLocation;
}

// Aliases for developer convenience
export const trackSessionLocation = requestAndTrackLocation;
export const updateSessionLocation = requestAndTrackLocation;
